/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useEffect, useState } from 'react';
import {
    func, string, object,
    arrayOf, shape, number, bool,
} from 'prop-types';
import { connect } from 'react-redux';
import { useUIDSeed } from 'react-uid';
import withStyles from '@material-ui/core/styles/withStyles';
import { Button } from '@material-ui/core';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import Fab from '@material-ui/core/Fab';
import { bindActionCreators } from 'redux';
import SubscriptionSummary from './SubscriptionSummary';
import configENV from '../../../config';
import { isEmpty } from '../../../utils/object';
import SubscriptionStatusChips from './SubscriptionStatusChips';
import EventDispatcher from '../../../state/reducers/EventDispatcher';
import SubscriptionFilterChips from './SubscriptionFilterChips';
import CancelSubsriptionDialog from '../Dialogs/CancelSubsriptionDialog';
import SkipSubsriptionDialog from '../Dialogs/SkipSubsriptionDialog';
import PauseSubsriptionDialog from '../Dialogs/PauseSubsriptionDialog';
import ResumeSubsriptionDialog from '../Dialogs/ResumeSubsriptionDialog';
import SkippedSubsriptionDialog from '../Dialogs/SkippedSubsriptionDialog';
import GenericSkeleton from '../../../../../components/GraphqlComponents/GraphqlSkeletonComponents/GenericSkeleton';
import SubscriptionDetail from './SubscriptionDetail';
import { trackEvent as track } from '../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';
import pastEndDate from '../Partials/CommonUtils';

const { PERSIST_KEY } = configENV;

const styles = (theme) => ({
    root: {
        width: '44px',
        height: '44px',
        background: theme.palette?.cms?.activeButtonColor || '#E8F1ED',
        '@media screen and (max-width: 400px)': {
            width: '36px',
            height: 0,
        },
    },
    list: {
        color: '#333',
    },
    card: {
        backgroundColor: theme.palette.white || '#fff',
        marginBottom: '20px',
        boxShadow: '0px 0px 10px rgba(47, 47, 47, 0.25)',
    },
    cardContent: {
        padding: '26px 24px 24px',
        '@media screen and (min-width: 1024px)': {
            padding: '32px 45px 32px 45px',
            '&:last-child': {
                paddingBottom: '32px',
            },
        },
        '@media screen and (min-width: 1440px)': {
            padding: '42px 58px 42px 58px',
            '&:last-child': {
                paddingBottom: '42px',
            },
        },
    },
    buttonContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'end',
        '-webkit-align-items': 'flex-end',
        '@media screen and (min-width: 1023px)': {
            display: 'none',
        },
    },
    subheadText: {
        marginBottom: '20px',
        fontStyle: 'italic',
        color: theme.palette.colorAlert,
        textAlign: 'center',
        '@media screen and (min-width: 768px)': {
            textAlign: 'inherit',
        },
    },
    errorText: {
        textAlign: 'center',
        '@media screen and (min-width: 768px)': {
            textAlign: 'inherit',
        },
    },
    subscriptionResMsg: {
        textAlign: 'center',
        fontSize: '1rem',
    },
    payUpFrontMessaging: {
        color: 'red',
        alignSelf: 'center',
        '@media screen and (max-width: 767px)': {
            width: '80%',
        },
    },
    btnContainer: {
        textAlign: 'center',
        margin: 'auto',
    },
    loadMoreBtn: {
        padding: '8px 20px',
        border: `1px solid ${theme.palette.black}`,
        '&:hover': {
            background: theme.palette.black,
            color: theme.palette.white,
        },
    },
});

function SubscriptionList({
    subscriptions, featureFlags, handleCancel, handleUpdate, classes,
    fetchSubscriptionGiftMessage, clearSubscriptionGiftMessage, isFetchingModal,
    subscriptionResMsg, trackEvent, hanldeLoadMore,
}) {
    const [mode, setMode] = useState('');
    const [subscriptionsList, setSubScriptionsList] = useState(subscriptions);
    const [currentSubscription, setCurrentSubscription] = useState('');
    const [activeFilter, setActiveFilter] = useState('ALL');
    const [openCancelModal, setOpenCancelModal] = useState(false);
    const [openSkipModal, setOpenSkipModal] = useState(false);
    const [openSkippedModal, setOpenSkippedModal] = useState(false);
    const [openPauseModal, setOpenPauseModal] = useState(false);
    const [openResumeModal, setOpenResumeModal] = useState(false);
    const [isFetchingSubscription, setIsFetchingSubscription] = useState(true);
    const [needToLogAbTest, setNeedToLogAbTest] = useState(true);

    const arrangeSubInOrder = () => {
        const activeSubscription = subscriptions.filter((subscription) => ['PROCESSED'].includes(subscription.subscriptionStatus) && !pastEndDate(subscription));
        const skippedSubscription = subscriptions.filter((subscription) => ['SKIPPED'].includes(subscription.subscriptionStatus) && !pastEndDate(subscription));
        const updatedSubscription = subscriptions.filter((subscription) => ['UPDATED'].includes(subscription.subscriptionStatus) && !pastEndDate(subscription));
        const resumedSubscription = subscriptions.filter((subscription) => ['RESUMED'].includes(subscription.subscriptionStatus) && !pastEndDate(subscription));
        const failedClubSubscription = subscriptions.filter((subscription) => ['CLUB_COMPONENT_FAILED'].includes(subscription.subscriptionStatus) && !pastEndDate(subscription));
        if (activeFilter === 'ACTIVE') return [...activeSubscription, ...updatedSubscription, ...resumedSubscription, ...skippedSubscription, ...failedClubSubscription];

        const pausedSubscription = subscriptions.filter((subscription) => ['PAUSED'].includes(subscription.subscriptionStatus) && !pastEndDate(subscription));
        if (activeFilter === 'PAUSED') return pausedSubscription;

        const cancelledSubscription = subscriptions.filter((subscription) => ['CANCELLED'].includes(subscription.subscriptionStatus) || pastEndDate(subscription));
        if (activeFilter === 'CANCELLED') return cancelledSubscription;

        return [...activeSubscription, ...updatedSubscription, ...resumedSubscription, ...skippedSubscription, ...pausedSubscription, ...cancelledSubscription];
    };

    useEffect(() => {
        if (subscriptions) {
            setIsFetchingSubscription(false);
            setSubScriptionsList(arrangeSubInOrder());
        }
    }, [subscriptions]);

    useEffect(() => {
        if (subscriptions) setSubScriptionsList(arrangeSubInOrder());
    }, [activeFilter]);

    const trackHandler = (eventActionValue, eventLabel) => {
        trackEvent({
            eventCategory: 'Subscriptions Management Portal',
            eventAction: eventActionValue || 'click',
            eventLabel: eventLabel || 'view',
        });
    };

    const handleSubscriptionSelectedFromList = (subscriptionRuleId) => {
        trackHandler('click', 'expand view');
        setMode('expand');
        clearSubscriptionGiftMessage();
        setCurrentSubscription(subscriptionRuleId);
        fetchSubscriptionGiftMessage(subscriptionRuleId);
    };

    const handleSubscriptionDetailClose = () => {
        trackHandler('click', 'minimize view');
        setMode('');
        setCurrentSubscription('');
        clearSubscriptionGiftMessage();
    };

    const gaGetButtonText = (buttonClicked) => (buttonClicked ? `${buttonClicked.substring(buttonClicked.indexOf('-') + 1)}` : '');

    const handleUpdateSubscription = (event, id, buttonClicked, flag) => {
        const eventLabel = gaGetButtonText(buttonClicked);
        switch (event) {
            case 'skip':
                trackHandler('click - Skip Modal', eventLabel);
                handleUpdate(event, id);
                setOpenSkipModal(false);
                setOpenSkippedModal(true);
                setOpenCancelModal(false);
                break;

            case 'pause':
                trackHandler('click - Pause Modal', eventLabel);
                handleUpdate(event, id);
                setOpenPauseModal(false);
                break;

            case 'resume':
                trackHandler('click - Resume Modal', eventLabel);
                handleUpdate(event, id);
                setOpenResumeModal(false);
                break;

            case 'cancel':
                trackHandler('click - Cancel Modal', 'YES, CANCEL MY SUBSCRIPTION');
                handleCancel(id);
                setOpenCancelModal(flag);
                break;

            default:
                setOpenSkipModal(false);
                setOpenPauseModal(false);
                setOpenResumeModal(false);
                setOpenCancelModal(false);
                break;
        }
    };

    const handleCancelModal = (status, subscriptionRuleId, buttonClicked) => {
        if (status && needToLogAbTest) {
            const label = featureFlags['sub-cancel-variant'] ? 'variant' : 'control';
            trackEvent({
                eventCategory: 'Test Impression',
                eventAction: 'click - Cancel Modal',
                eventLabel: label,
            });
            setNeedToLogAbTest(false);
        }
        const eventLabel = gaGetButtonText(buttonClicked);
        if (status && buttonClicked !== 'Cancel') trackHandler('click - Skip Modal', eventLabel);
        else trackHandler('click - Cancel Modal', eventLabel);
        setOpenCancelModal(status);
        if (status) setCurrentSubscription(subscriptionRuleId);
        else setCurrentSubscription('');
    };

    const handleCancelSkipModal = (status, subscriptionRuleId) => {
        trackHandler('click - Cancel Modal', 'SKIP NEXT DELIVERY');
        handleUpdateSubscription(status, subscriptionRuleId);
    };

    const handleSkipModal = (status, subscriptionRuleId, buttonClicked) => {
        const eventLabel = gaGetButtonText(buttonClicked);
        trackHandler('click - Skip Modal', eventLabel);
        setOpenSkipModal(status);
        if (status) setCurrentSubscription(subscriptionRuleId);
        else setCurrentSubscription('');
    };

    const handleSkippedModal = (status, subscriptionRuleId, buttonClicked) => {
        trackHandler('click - Skipped Modal', gaGetButtonText(buttonClicked));
        setOpenSkippedModal(status);
        if (status) setCurrentSubscription(subscriptionRuleId);
        else setCurrentSubscription('');
    };

    const handlePauseModal = (status, subscriptionRuleId, buttonClicked) => {
        trackHandler('click - Pause Modal', gaGetButtonText(buttonClicked));
        setOpenPauseModal(status);
        if (status) setCurrentSubscription(subscriptionRuleId);
        else setCurrentSubscription('');
    };

    const handleResumeModal = (status, subscriptionRuleId, buttonClicked) => {
        trackHandler('click - Resume Modal', gaGetButtonText(buttonClicked));
        setOpenResumeModal(status);
        if (status) setCurrentSubscription(subscriptionRuleId);
        else setCurrentSubscription('');
    };

    const updateSubscriptionList = (subscription) => {
        const index = subscriptionsList.findIndex((x) => x.ruleId === subscription.ruleId);
        subscriptionsList[index] = subscription;
    };

    const enableEditButton = (selectedSubscription) => {
        if (pastEndDate(selectedSubscription)) return false;
        if (selectedSubscription.subscriptionStatus === 'CANCELLED') return false;
        if (selectedSubscription?.subscriptionType?.toLowerCase() === 'monthonlypayfull') return false;
        return true;
    };

    const isPayupFrontClub = (selectedSubscription) => selectedSubscription?.subscriptionType?.toLowerCase() === 'monthonlypayfull';
    const subHead = <div className={classes.subheadText}>Your <u>default credit card</u> will be used for Subscription orders.</div>;
    const noOfLoader = [1, 2, 3];
    const seed = useUIDSeed();

    const setActiveFilterWithGA = (selectedFilter) => {
        trackHandler(null, `${selectedFilter} - view`);
        setActiveFilter(selectedFilter);
    };

    return (
        <>
            <SubscriptionFilterChips handleFilter={setActiveFilterWithGA} activeFilter={activeFilter} />
            {subHead}
            <div className={classes.list}>
                {isFetchingSubscription && noOfLoader.map((i) => <GenericSkeleton key={seed(i)} height="200px" width="100%" />) }
                {(!subscriptionsList || isEmpty(subscriptionsList)) && !isFetchingSubscription && <div className={classes.errorText}>You have no {activeFilter !== 'ALL' && activeFilter} subscriptions.</div>}
                {!isEmpty(subscriptionsList) && subscriptionsList?.map((subscription) => (
                    <Card className={classes.card} key={`${subscription.ruleId}`}>
                        <CardContent classes={{ root: classes.cardContent }}>
                            <SubscriptionSummary
                                subscription={subscription}
                                includeArrow
                                mode={mode}
                                handleCancelModal={handleCancelModal}
                                handleSkipModal={handleSkipModal}
                                handlePauseModal={handlePauseModal}
                                handleResumeModal={handleResumeModal}
                                currentSubscription={currentSubscription}
                                handleExpand={handleSubscriptionSelectedFromList}
                                handleMinimize={handleSubscriptionDetailClose}
                                pastEndDate={pastEndDate(subscription)}
                            />
                            {   subscription.subscriptionStatus !== 'CANCELLED' && !pastEndDate(subscription)
                            && (
                                <div className={classes.buttonContainer}>
                                    { !isPayupFrontClub(subscription) ? (
                                        <SubscriptionStatusChips
                                            subscription={subscription}
                                            handleCancelModal={handleCancelModal}
                                            handleSkipModal={handleSkipModal}
                                            handlePauseModal={handlePauseModal}
                                            handleResumeModal={handleResumeModal}
                                        />
                                    ) : <div className={classes.payUpFrontMessaging}>Please contact customer service if you would like to cancel or update this subscription product</div>}
                                    { mode === 'expand' && currentSubscription === subscription.ruleId
                                        ? <Fab className={classes.root} onClick={() => handleSubscriptionDetailClose()}><RemoveIcon /></Fab>
                                        : <Fab className={classes.root} onClick={() => handleSubscriptionSelectedFromList(subscription.ruleId)}><AddIcon /></Fab>}
                                </div>
                            )}
                            {
                                mode === 'expand' && currentSubscription === subscription.ruleId && (
                                    <SubscriptionDetail
                                        subscriptionRuleId={currentSubscription}
                                        selectedSubscription={subscription}
                                        handleCancel={handleCancel}
                                        handleUpdate={handleUpdate}
                                        featureFlags={featureFlags}
                                        updateSubscriptionList={updateSubscriptionList}
                                        enableEditButton={enableEditButton}
                                    />
                                )
                            }
                            <CancelSubsriptionDialog
                                handleCancelModal={handleCancelModal}
                                handleCancel={handleUpdateSubscription}
                                openModal={openCancelModal}
                                selectedSubscription={currentSubscription}
                                subscription={subscription}
                                featureFlags={featureFlags}
                                handleCancelSkipModal={handleCancelSkipModal}
                                trackHandler={trackHandler}
                            />
                            <SkipSubsriptionDialog
                                handleSkipModal={handleSkipModal}
                                handleUpdate={handleUpdateSubscription}
                                openModal={openSkipModal}
                                selectedSubscription={currentSubscription}
                                subscription={subscription}
                            />
                            <SkippedSubsriptionDialog
                                handleSkippedModal={handleSkippedModal}
                                handleUpdate={handleUpdateSubscription}
                                openModal={openSkippedModal}
                                selectedSubscription={currentSubscription}
                                subscription={subscription}
                            />
                            <PauseSubsriptionDialog
                                handlePauseModal={handlePauseModal}
                                handleUpdate={handleUpdateSubscription}
                                openModal={openPauseModal}
                                selectedSubscription={currentSubscription}
                                subscription={subscription}
                            />
                            <ResumeSubsriptionDialog
                                handleResumeModal={handleResumeModal}
                                handleUpdate={handleUpdateSubscription}
                                openModal={openResumeModal}
                                selectedSubscription={currentSubscription}
                                subscription={subscription}
                            />
                        </CardContent>
                    </Card>
                ))}
                {!isEmpty(subscriptionsList) && (isFetchingModal && subscriptionResMsg === '' ? noOfLoader.map((i) => <GenericSkeleton key={seed(i)} height="200px" width="100%" />) : <div className={classes.subscriptionResMsg}>{subscriptionResMsg}</div>)}
                {!isEmpty(subscriptionsList) && !isFetchingModal && subscriptionResMsg === '' && (
                    <div className={classes.btnContainer}>
                        <Button onClick={hanldeLoadMore} className={classes.loadMoreBtn}>Load More</Button>
                    </div>
                )}
            </div>
        </>
    );
}

SubscriptionList.defaultProps = {
    subscriptions: null,
    handleCancel: () => {},
    handleUpdate: () => {},
    isFetchingModal: false,
    subscriptionResMsg: '',
};

SubscriptionList.propTypes = {
    isFetchingModal: bool,
    hanldeLoadMore: func.isRequired,
    subscriptions: arrayOf(
        shape({
            checkoutBrand: string,
            duration: number.isRequired,
            endDate: string.isRequired,
            interval: number.isRequired,
            nextOrderDate: string,
            orderDate: string.isRequired,
            orderHistoryResult: arrayOf(
                shape({
                    deliveryDate: string,
                    discountAmount: string,
                    orderDate: string.isRequired,
                    orderNumber: string.isRequired,
                    productCode: string.isRequired,
                    productName: string.isRequired,
                    productPrice: string.isRequired,
                    quantity: string.isRequired,
                }),
            ),
            orderNumber: string.isRequired,
            orderTime: string.isRequired,
            orderType: string.isRequired,
            price: string.isRequired,
            productId: string.isRequired,
            productImageCategory: string.isRequired,
            productImageThumbnail: string.isRequired,
            productName: string.isRequired,
            quantity: string,
            recipient: arrayOf(shape({
                city: string.isRequired,
                country: string.isRequired,
                firstName: string.isRequired,
                lastName: string.isRequired,
                state: string.isRequired,
                street1: string.isRequired,
                street2: string.isRequired,
                zipcode: string.isRequired,
            })),
            ruleId: string.isRequired,
            startDate: string.isRequired,
            subscriptionStatus: string.isRequired,
            totalAmount: string.isRequired,
        }),
    ),
    handleCancel: func,
    handleUpdate: func,
    classes: object.isRequired,
    featureFlags: shape({
        'is-subscription-skip': bool,
        'is-subscription-pause': bool,
        'is-subscription-frequency': bool,
    }).isRequired,
    fetchSubscriptionGiftMessage: func.isRequired,
    clearSubscriptionGiftMessage: func.isRequired,
    subscriptionResMsg: string,
    trackEvent: func.isRequired,
};

const mapStateToProps = (state) => {
    const newState = state[PERSIST_KEY] ? state[PERSIST_KEY] : state;
    return {
        subscriptions: newState.subscriptions,
        subscriptionResMsg: newState.subscriptionResMsg,
        toggleState: newState.toggleState,
    };
};
const mapDispatchToProps = (dispatch) => ({
    trackEvent: bindActionCreators(track, dispatch),
    clearSubscriptionGiftMessage: () => dispatch(EventDispatcher.clearSubscriptionGiftMessage()),
    fetchSubscriptionGiftMessage: (ruleId) => dispatch(EventDispatcher.fetchSubscriptionGiftMessage(ruleId)),
});

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withStyles(styles, { withTheme: true })(SubscriptionList));
